/**
 * Fund + location + currency picker — the three axes, in one control.
 *
 * The combination need not already exist: the server creates the account on
 * first use. That is what keeps setup from being a chore, so this must not
 * restrict the choices to combinations that already have money in them.
 */
import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/misc';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCurrencies, useFunds, useLocations, useBalances } from '@/hooks/useApi';
import { colorClasses } from '@/lib/colors';
import { formatMoney } from '@/lib/format';
import type { AccountRef } from '@/lib/types';

export function AccountPicker({
  value,
  onChange,
  label,
  showCurrency = true,
  lockCurrency,
  lockFund,
  className,
  idPrefix = 'acc',
}: {
  value: Partial<AccountRef>;
  onChange: (next: Partial<AccountRef>) => void;
  label?: string;
  showCurrency?: boolean;
  /** Fix the currency (repayments must match the loan). */
  lockCurrency?: string;
  lockFund?: number;
  className?: string;
  idPrefix?: string;
}): JSX.Element {
  const { data: funds = [] } = useFunds();
  const { data: locations = [] } = useLocations();
  const { data: currencies = [] } = useCurrencies();
  const { data: balances } = useBalances();

  const currency = lockCurrency ?? value.currency;

  /**
   * Balance of the exact combination selected, so the user sees what is
   * actually there before moving money out of it. Undefined means the account
   * does not exist yet — shown as "нова сметка", not as zero, because those
   * are different facts.
   */
  const currentBalance = useMemo(() => {
    if (!balances || !value.fundId || !value.locationId || !currency) return undefined;
    return balances.accounts.find(
      (a) => a.fundId === value.fundId && a.locationId === value.locationId && a.currency === currency,
    );
  }, [balances, value.fundId, value.locationId, currency]);

  return (
    <div className={cn('space-y-2', className)}>
      {label ? <Label>{label}</Label> : null}

      <div className={cn('grid gap-2', showCurrency ? 'grid-cols-[1fr_1fr_auto]' : 'grid-cols-2')}>
        <Select
          value={value.fundId ? String(value.fundId) : ''}
          onValueChange={(v) => onChange({ ...value, fundId: Number(v) })}
          disabled={lockFund != null}
        >
          <SelectTrigger id={`${idPrefix}-fund`} aria-label="Каса">
            <SelectValue placeholder="Каса" />
          </SelectTrigger>
          <SelectContent>
            {funds.map((f) => (
              <SelectItem key={f.id} value={String(f.id)}>
                <span className="flex items-center gap-2">
                  <span className={cn('h-2 w-2 shrink-0 rounded-full', colorClasses(f.color).bg)} />
                  {f.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={value.locationId ? String(value.locationId) : ''}
          onValueChange={(v) => onChange({ ...value, locationId: Number(v) })}
        >
          <SelectTrigger id={`${idPrefix}-location`} aria-label="Място">
            <SelectValue placeholder="Място" />
          </SelectTrigger>
          <SelectContent>
            {locations.map((l) => (
              <SelectItem key={l.id} value={String(l.id)}>
                {l.name}
                {l.holderName ? (
                  <span className="ml-1 text-xs text-muted-foreground">({l.holderName})</span>
                ) : null}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {showCurrency ? (
          <Select
            value={currency ?? ''}
            onValueChange={(v) => onChange({ ...value, currency: v })}
            disabled={lockCurrency != null}
          >
            <SelectTrigger id={`${idPrefix}-currency`} className="w-24" aria-label="Валута">
              <SelectValue placeholder="Вал." />
            </SelectTrigger>
            <SelectContent>
              {currencies.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.symbol} {c.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
      </div>

      {value.fundId && value.locationId && currency ? (
        <p className="text-xs text-muted-foreground">
          {currentBalance ? (
            <>
              Налично:{' '}
              <span className="tabular font-medium text-foreground">
                {formatMoney(currentBalance.nativeMinor, currency, currentBalance.decimals)}
              </span>
            </>
          ) : (
            'Нова сметка — ще бъде създадена при записа'
          )}
        </p>
      ) : null}
    </div>
  );
}

// Re-exported so callers can import the picker and its guard together; the
// single definition lives with the rest of the movement rules.
export { isCompleteRef } from '@/lib/movement';
